import { buttonStates } from './input';

import { BASE_LINE_HEIGHT_MOD } from './constants';
import { COLORS } from './constants-colors';
import { addGameObject, getZeroEntity, setEntityPosition } from './entity';

import { Alignment_Horizontal, ObjectType } from './shared/api_shared_types';
import { mouseDownThisFrame, setVisibilityState } from './simulate';
import {
  VisibilityState,
  GameObjectController,
  MoveMode,
  RenderItemType,
  Shape,
  TextEditor,
  TextObject,
  UserTextEditor,
} from './types';
import { colorToHex, isDarkColor } from './helpers';
import { setUserTextFocus } from './user_text';

export function controller(): GameObjectController {
  return {
    getNew: null,
    sync: null,
    update,
    dispose,
  };
}

function dispose(obj: TextEditor) {
  obj.inputElement?.remove();
}

export function stopTextEdit(editor: TextEditor) {
  setVisibilityState(editor, VisibilityState.DISAPPEARING);
  editor.stopEditingThisFrame = true;
}

export function createTextEditor(tObj: TextObject, maxLength: number = 400): TextEditor {
  const manager = tObj.manager;

  let newTextEditor: TextEditor = {
    ...getZeroEntity(),
    objectType: ObjectType.TEXT_EDITOR,

    tObj,
    inputElement: null,
    stopEditingThisFrame: false,
    previousMoveMode: MoveMode.NONE,
    autoFinishEditor: false,
    showBorder: true,
    hasFocus: false,
  };

  newTextEditor.clickable = true;
  newTextEditor.width = tObj.textEnt.width;
  newTextEditor.shape = Shape.RECT;
  newTextEditor.moveMode = MoveMode.STATIC;
  newTextEditor.allowDrag = false;

  addGameObject(manager, newTextEditor);

  setVisibilityState(newTextEditor, VisibilityState.DEFAULT);

  let element = document.createElement('textarea');
  element.classList.add('text-editor');

  const fontSizePx = tObj.fontSizePx;
  const lineHeightPx = fontSizePx * BASE_LINE_HEIGHT_MOD * tObj.textStyle.lineHeightMod;

  const diff = lineHeightPx - fontSizePx;

  newTextEditor.height = tObj.textEnt.height + diff;

  element.style.fontSize = `${fontSizePx}px`;

  const textColor = tObj.textStyle.defaultTextColor;
  element.style.color = colorToHex(textColor);

  if (isDarkColor(textColor)) {
    element.classList.add('selection-light');
  } else {
    element.classList.add('selection-dark');
  }

  element.style.lineHeight = `${lineHeightPx}px`;
  element.style.textAlign = tObj.textStyle.textAlign.toString();
  element.style.fontFamily = tObj.textStyle.fontId;

  element.maxLength = maxLength;
  element.value = tObj.text;

  //Prevent normal enter in text-area, since enter will finalize text-edit mode
  element.addEventListener('keypress', (event: KeyboardEvent) => {
    if (!event.altKey && !event.ctrlKey && !event.shiftKey && event.key === 'Enter') {
      event.preventDefault();
    }
  });

  newTextEditor.inputElement = element;

  element.addEventListener('blur', () => {
    newTextEditor.hasFocus = false;
    if (newTextEditor === manager.cursor.textEditor) {
      setUserTextFocus(manager.cursor.userText, false);
    }
  });
  element.addEventListener('focus', () => {
    newTextEditor.hasFocus = true;
    if (newTextEditor === manager.cursor.textEditor) {
      setUserTextFocus(manager.cursor.userText, true);
    }
  });

  //We have to wait for the element to be loaded until we can set focus...
  //There's probably a nicer way to do this: https://stackoverflow.com/questions/5525071/how-to-wait-until-an-element-exists
  setTimeout(() => element.focus(), 10);

  manager.DOM.overlayContainer.appendChild(element);

  return newTextEditor;
}

function update(editor: TextEditor): boolean {
  if (editor.autoFinishEditor) {
    if (mouseDownThisFrame() && !editor.mouseDownOnMe) {
      stopTextEdit(editor);
    }

    if (
      buttonStates.ENTER.changedThisFrame &&
      buttonStates.ENTER.isDown &&
      !buttonStates.SHIFT.isDown
    ) {
      stopTextEdit(editor);
    }
  }

  if (editor.visibilityState === VisibilityState.DISAPPEARING) {
    editor.inputElement.style.display = 'none';
  }

  return false;
}

export function updateTextEditorTransform(editor: TextEditor) {
  const tObj = editor.tObj;
  const fontSizePx = tObj.fontSizePx;
  const lineHeightPx = fontSizePx * BASE_LINE_HEIGHT_MOD * tObj.textStyle.lineHeightMod;

  editor.inputElement.style.fontSize = `${fontSizePx}px`;
  editor.inputElement.style.lineHeight = `${lineHeightPx}px`;

  //This correction is needed to avoid the word-wrap applied too soon
  //in the text input element.
  const TEXT_INPUT_WIDTH_CORRECTION = fontSizePx / 8;

  editor.width = tObj.textEnt.width + TEXT_INPUT_WIDTH_CORRECTION;

  //This diff is to take correct for the line-height that is ignored in text objects
  //avove the first line and after the last line.
  //The text input element does use the line height for first and last line, so we need to
  //corrent the total height to avoid different line positions.
  const diff = lineHeightPx - fontSizePx;
  editor.height = tObj.textEnt.height + diff;

  const textAlign = tObj.textStyle.textAlign;

  //Since the input area is slightly wider that the actual text object,
  //we have to corect the position as well, based on how the text is aligned.
  const TEXT_EDIT_X_CORRECTION =
    textAlign === Alignment_Horizontal.LEFT
      ? 0
      : textAlign === Alignment_Horizontal.CENTER
      ? -TEXT_INPUT_WIDTH_CORRECTION / 2
      : -TEXT_INPUT_WIDTH_CORRECTION;

  const x = tObj.textEnt.pos.x + TEXT_EDIT_X_CORRECTION + editor.width / 2;

  const TEXT_EDIT_Y_CORRECTION = -3;
  const y = tObj.textEnt.pos.y + TEXT_EDIT_Y_CORRECTION + editor.height / 2;

  setEntityPosition(editor, x, y);
}

// function createTextObjectEditorView(renderGroup: RenderGroup, editor: UserTextEditor) {
//   let pos = editor.pos;

//   let group = createAndInsertNewRenderItemInGroup(
//     RenderItemType.group,
//     editor.localZIndex,
//     renderGroup,
//   ) as RenderGroup;

//   {
//     let rect = addNextRenderItemToGroup(RenderItemType.rect, group) as RenderRect;
//     rect.position = pos;
//     rect.width = editor.width;
//     rect.height = editor.height;
//     rect.shapeStyle.fillColor = [0, 0, 0, 0];
//     rect.shapeStyle.lineDash = [5, 5, 5];
//     rect.shapeStyle.strokeWeight = 1;
//     rect.shapeStyle.strokeColor = COLORS.WHITE_100;
//   }
// }
